import { useCallback, useEffect, useRef } from "react";
import { buildTutorSpeechSnapshot } from "@/features/tutor/utils/tutorSpeechSnapshot";
import { useAudioStore } from "@/features/tutor/store/useAudioStore";
import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext";
import useLogger from "./useLogger";
import useRecorder from "./useRecorder";
import { useNotificationStore } from "@/store/useNotificationStore";
import { useModalStore, ModalType } from "@/store/useModalStore";
import { useMicPermissionStore } from "@/features/tutor/store/useMicPermissionStore";
import { restorePageFocus } from "@/utils/audioUtils";
import {
  BUFFER_SIZE,
  DEFAULT_STOP_DEBOUNCE_MS,
  SILENCE_PRESS_DURATION_MIN_MS,
  getErrorName,
  getMicAudioConstraints,
  getMicCaptureProfile,
  isAudioGraphStale,
  isKnownMicErrorName,
  isPcmBufferAllZero,
  isPermissionDeniedErrorName,
  isPermissionDeniedState,
  loadPreferredMicDeviceId,
  savePreferredMicDeviceId,
  shouldShowMicModalForLastError,
} from "@/features/tutor/vo-demo/audio/microphone";
import { getPendingMicPermissionRequest } from "@/features/tutor/vo-demo/audio/micPermissionGestureRequest";
import {
  CaptureGate,
  GATE_ARMED,
  GATE_IDLE,
} from "@/features/tutor/vo-demo/audio/captureGate";

const DEBUG_AUDIO = import.meta.env.VITE_DEBUG === "true";

export const useMicrophone = ({
  enabled = true,
  stopDebounceMs = DEFAULT_STOP_DEBOUNCE_MS,
} = {}) => {
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const workletNodeRef = useRef(null);
  const isInitializedRef = useRef(false);
  const messageHandlerSetRef = useRef(false);
  const shouldStopRef = useRef(false);
  const intentionalCleanupRef = useRef(false);
  const stopDebounceTimerRef = useRef(null);

  const isRecordingRef = useRef(false);
  const isInitializingRef = useRef(false);
  // Bumped by every cleanup. An initialization that awaits across one --
  // a permission prompt still open when the player unmounts -- finds the
  // number changed and releases what it acquired instead of keeping it.
  const initGenerationRef = useRef(0);

  // Routes worklet audio between buffer-locally (armed, pre-threshold) and
  // emit-live (committed turn). See captureGate.js for why.
  const captureGateRef = useRef(null);
  if (captureGateRef.current === null) {
    captureGateRef.current = new CaptureGate();
  }
  // Bumped on every worklet "start" and echoed back in final_chunk_sent, so a
  // stale final from a capture that was stopped and immediately superseded
  // (tap-abort followed by the synthetic submit press) can't finalize the new
  // turn prematurely.
  const captureIdRef = useRef(0);

  const lastMicErrorTimeRef = useRef(0);

  // Per-turn silence-detection state.
  const pressStartTimeRef = useRef(0);
  // Timestamp of the button/key release, captured when stopRecording is
  // requested (before the debounce delay). Used to measure the true hold
  // duration so a quick tap-to-submit isn't misread as an all-zero speech turn.
  const releaseTimeRef = useRef(0);
  const turnHadNonZeroSampleRef = useRef(false);

  const { setIsRecording, setMicCaptureProfile } = useAudioStore();
  const { emit } = useRuntimeContext();
  const { addLog } = useLogger();
  const { showNotification } = useNotificationStore();
  const { openModal, closeModal } = useModalStore();
  const { setMicPermissionDenied, setMicMuted } = useMicPermissionStore();

  const {
    isRecording: isRecorderRecording,
    startRecording: startRecorderRecording,
    stopRecording: stopRecorderRecording,
    audioBlob,
  } = useRecorder();

  const retryMicPermissionRef = useRef(null);
  const isRetryingRef = useRef(false);

  const showMicModal = useCallback(() => {
    const now = Date.now();
    if (
      !shouldShowMicModalForLastError({
        lastErrorTimeMs: lastMicErrorTimeRef.current,
        nowMs: now,
      })
    ) {
      return;
    }
    lastMicErrorTimeRef.current = now;
    openModal(ModalType.MIC_PERMISSION, {
      onRetry: retryMicPermissionRef.current,
    });
  }, [openModal]);

  const initializeAudio = useCallback(async () => {
    if (isInitializedRef.current || isInitializingRef.current) {
      addLog("Audio already initialized or initializing, skipping...");
      return;
    }

    const generation = initGenerationRef.current;
    const superseded = () => initGenerationRef.current !== generation;
    try {
      intentionalCleanupRef.current = false;
      addLog("Starting audio initialization...");
      isInitializingRef.current = true;
      // Await only when one is pending: an extra microtask before getUserMedia
      // can cost a retry its user gesture.
      const pendingGestureRequest = getPendingMicPermissionRequest();
      if (pendingGestureRequest) await pendingGestureRequest;
      if (superseded()) return;
      const preferredDeviceId = loadPreferredMicDeviceId();
      let micConstraints = getMicAudioConstraints(preferredDeviceId);
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(micConstraints);
      } catch (err) {
        // Stored deviceId may reference a device that no longer exists
        // (e.g. unplugged headphones). Clear it and fall back to default mic.
        if (
          !superseded() &&
          preferredDeviceId &&
          getErrorName(err) === "OverconstrainedError"
        ) {
          addLog("Preferred mic device unavailable - falling back to default");
          savePreferredMicDeviceId(null);
          micConstraints = getMicAudioConstraints(null);
          stream = await navigator.mediaDevices.getUserMedia(micConstraints);
        } else {
          throw err;
        }
      }
      if (superseded()) {
        // Cleaned up while the prompt was open -- the player unmounted, or a
        // retry started over. Nothing else holds this stream to stop it.
        stream.getTracks().forEach((track) => track.stop());
        addLog("Audio initialization superseded - released its stream");
        return;
      }
      mediaStreamRef.current = stream;
      // Successful stream acquisition means mic is fully available now.
      setMicCaptureProfile(
        getMicCaptureProfile(mediaStreamRef.current, micConstraints),
      );
      setMicPermissionDenied(false);
      setMicMuted(false);

      mediaStreamRef.current.getTracks().forEach((track) => {
        track.onended = () => {
          if (!intentionalCleanupRef.current) {
            console.error(
              "ERROR: Audio track ended - microphone disconnected!",
            );
            setMicPermissionDenied(true);
            // Safari steals page focus when it revokes mic permission.
            // Restore it immediately so window.keydown still fires.
            restorePageFocus();
          }
          isInitializedRef.current = false;
          setMicCaptureProfile(null);
        };

        // Safari fires onmute/onunmute for mic interruptions (address bar toggle, phone call, screen lock).
        // Show the MicToast while muted; hide it when the track restores.
        // Uses micMuted (not micPermissionDenied) to avoid triggering session re-init logic.
        track.onmute = () => {
          if (!intentionalCleanupRef.current) {
            addLog("Audio track muted - showing mic unavailable UI");
            setMicMuted(true);
            if (stopDebounceTimerRef.current) {
              clearTimeout(stopDebounceTimerRef.current);
              stopDebounceTimerRef.current = null;
            }
            isRecordingRef.current = false;
            setIsRecording(false);
            // Safari steals page focus when it mutes the mic track.
            // Restore it so window.keydown still fires.
            restorePageFocus();
          }
        };

        track.onunmute = () => {
          addLog("Audio track unmuted - mic restored");
          setMicMuted(false);
        };
      });

      audioContextRef.current = new (
        window.AudioContext || window.webkitAudioContext
      )();

      await audioContextRef.current.audioWorklet.addModule(
        "/audio/audio-processor.worklet.js",
      );
      // The stream and context were in their refs across that await, so the
      // cleanup that superseded this already released them.
      if (superseded()) return;

      sourceRef.current = audioContextRef.current.createMediaStreamSource(
        mediaStreamRef.current,
      );

      const analyserNode = audioContextRef.current.createAnalyser();
      analyserNode.fftSize = 256;
      analyserNode.smoothingTimeConstant = 0.8;
      sourceRef.current.connect(analyserNode);
      window.__micAnalyserNode = analyserNode;

      workletNodeRef.current = new AudioWorkletNode(
        audioContextRef.current,
        "audio-processor",
        {
          processorOptions: {
            bufferSize: BUFFER_SIZE,
          },
        },
      );

      if (!messageHandlerSetRef.current) {
        workletNodeRef.current.port.onmessage = (event) => {
          const msg = event.data;

          if (msg.type === "audio") {
            if (
              !turnHadNonZeroSampleRef.current &&
              !isPcmBufferAllZero(msg.buffer)
            ) {
              turnHadNonZeroSampleRef.current = true;
              // Mic is clearly working again - clear any lingering silence toast.
              if (useMicPermissionStore.getState().silenceToastVisible) {
                useMicPermissionStore.getState().setSilenceToastVisible(false);
              }
            }
            // Armed: buffered locally until the turn opens. Live: emitted.
            // Idle: a stale flush from an aborted arm — dropped.
            captureGateRef.current.onAudioChunk(msg, (m) =>
              emit("USER_AUDIO_CHUNK", m),
            );
          } else if (msg.type === "final_chunk_sent") {
            // A final from a superseded capture (the worklet was re-started
            // after this stop was requested) must not finalize the current
            // turn. Undefined capture_id means a legacy cached worklet — let
            // it through rather than never finalizing.
            if (
              msg.capture_id !== undefined &&
              msg.capture_id !== null &&
              msg.capture_id !== captureIdRef.current
            ) {
              addLog(
                `Final chunk from superseded capture ${msg.capture_id} (current ${captureIdRef.current}) — swallowed`,
              );
              return;
            }
            // Only a committed (live) turn may finalize. An aborted arm also
            // stops the worklet and produces this marker; emitting DONE for it
            // would finalize a turn that was never opened.
            if (!captureGateRef.current.onFinalChunk()) {
              addLog(
                `Final chunk (chunk ${msg.chunk_idx}) from an aborted arm — swallowed`,
              );
              return;
            }
            addLog(
              `Final chunk sent (chunk ${msg.chunk_idx}), stopping recorder and emitting USER_AUDIO_DONE`,
            );

            if (DEBUG_AUDIO) {
              stopRecorderRecording();
            }

            emit("USER_AUDIO_DONE");
          } else if (msg.type === "started") {
            addLog(`Worklet started: ${msg.message}`);
          }
        };
        messageHandlerSetRef.current = true;
        addLog("Message handler set up");
      }

      sourceRef.current.connect(workletNodeRef.current);
      workletNodeRef.current.connect(audioContextRef.current.destination);

      isInitializingRef.current = false;
      isInitializedRef.current = true;
      addLog("Audio initialized successfully");
    } catch (err) {
      // A newer initialization owns the flags, and an unmounted player has
      // no one to show a mic error to.
      if (superseded()) return;
      isInitializingRef.current = false;
      addLog("Error in initializeAudio: " + err);
      const errorName = getErrorName(err);
      if (isPermissionDeniedErrorName(errorName)) {
        setMicPermissionDenied(true);
      }
      throw err;
    }
  }, [
    addLog,
    emit,
    setMicPermissionDenied,
    setMicMuted,
    setIsRecording,
    stopRecorderRecording,
    setMicCaptureProfile,
  ]);

  useEffect(() => {
    if (!enabled) return;
    initializeAudio().catch(() => {
      lastMicErrorTimeRef.current = 0;
      showMicModal();
    });
  }, [enabled, initializeAudio, showMicModal]);

  // Internal function that actually stops recording
  const doStopRecording = useCallback(() => {
    try {
      shouldStopRef.current = true;
      addLog("Actually stopping recording...");
      isRecordingRef.current = false;
      setIsRecording(false);

      if (workletNodeRef.current) {
        workletNodeRef.current.port.postMessage({ type: "stop" });
        addLog("Sent stop message to worklet");
      }

      const pressStart = pressStartTimeRef.current;
      pressStartTimeRef.current = 0;
      // Measure the hold from press to release, not to now: doStopRecording runs
      // after the stop debounce (stopDebounceMs), so using Date.now() here would
      // inflate a quick tap past SILENCE_PRESS_DURATION_MIN_MS and falsely prompt
      // the mic-silence toast. releaseTimeRef is stamped at release time.
      const releaseTime = releaseTimeRef.current;
      releaseTimeRef.current = 0;
      const pressDuration =
        pressStart && releaseTime ? releaseTime - pressStart : 0;
      const { micUnavailable, silenceToastVisible, setSilenceToastVisible } =
        useMicPermissionStore.getState();
      if (
        pressDuration >= SILENCE_PRESS_DURATION_MIN_MS &&
        !turnHadNonZeroSampleRef.current &&
        !micUnavailable &&
        !silenceToastVisible &&
        !useModalStore.getState().modal
      ) {
        addLog("All-zero audio for entire turn - showing mic test toast");
        setSilenceToastVisible(true);
      }
    } catch (err) {
      addLog("Error in doStopRecording: " + err);
      isRecordingRef.current = false;
      setIsRecording(false);
    }
  }, [addLog, setIsRecording]);

  // Debounced stop - each call resets the timer, only stops after stopDebounceMs of no activity
  const stopRecording = useCallback(
    (immediate = false) => {
      // Clear any pending stop timer
      if (stopDebounceTimerRef.current) {
        clearTimeout(stopDebounceTimerRef.current);
        stopDebounceTimerRef.current = null;
      }

      // If not recording, nothing to do
      if (!isRecordingRef.current) {
        return;
      }

      // Stamp the release time now (before any debounce) so doStopRecording can
      // measure the true hold duration for silence detection.
      releaseTimeRef.current = Date.now();

      // Immediate stop (for cleanup/visibility change)
      if (immediate) {
        addLog("Immediate stop requested");
        doStopRecording();
        return;
      }

      // Debounced stop - schedule it for later
      addLog(`Scheduling debounced stop in ${stopDebounceMs}ms...`);
      stopDebounceTimerRef.current = setTimeout(() => {
        stopDebounceTimerRef.current = null;
        doStopRecording();
      }, stopDebounceMs);
    },
    [addLog, doStopRecording, stopDebounceMs],
  );

  const cleanup = useCallback(() => {
    initGenerationRef.current += 1;
    try {
      shouldStopRef.current = true;
      intentionalCleanupRef.current = true;

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      setMicCaptureProfile(null);

      if (workletNodeRef.current) {
        workletNodeRef.current.port.postMessage({ type: "stop" });
        workletNodeRef.current.disconnect();
        workletNodeRef.current = null;
      }

      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }

      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      window.__micAnalyserNode = null;
      isInitializedRef.current = false;
      isInitializingRef.current = false;
      messageHandlerSetRef.current = false;
      captureGateRef.current.reset();
      addLog("Audio cleanup completed");
    } catch (err) {
      addLog("Error in cleanup: " + err);
    }
  }, [addLog, setMicCaptureProfile]);

  const retryMicPermission = useCallback(async () => {
    if (isRetryingRef.current) return;
    isRetryingRef.current = true;
    // Closed before the prompt opens; a failure below reopens it.
    closeModal();
    cleanup();
    let succeeded = false;
    try {
      await initializeAudio();
      succeeded = true;
    } catch (err) {
      lastMicErrorTimeRef.current = 0;
      showMicModal();
    } finally {
      isRetryingRef.current = false;
    }
    if (succeeded) {
      closeModal();
      // Safari steals page focus after the getUserMedia dialog closes.
      // setTimeout(0) defers until after React re-renders from closeModal(),
      // at which point the hidden-input trick reliably reclaims focus.
      setTimeout(restorePageFocus, 0);
    }
  }, [cleanup, initializeAudio, closeModal, showMicModal]);

  retryMicPermissionRef.current = retryMicPermission;

  // Re-init the audio graph after the user picks a new device. The new
  // deviceId was persisted to localStorage by the modal; initializeAudio
  // picks it up on the next call.
  const onSilenceDeviceConfirmed = useCallback(async () => {
    addLog("Silence-test device confirmed - reinitializing audio graph");
    cleanup();
    try {
      await initializeAudio();
    } catch (err) {
      addLog("Reinit after device switch failed: " + err);
      lastMicErrorTimeRef.current = 0;
      showMicModal();
    }
  }, [addLog, cleanup, initializeAudio, showMicModal]);

  // Register reinit callback in the store so ProfileDropdown can trigger it
  // without needing a prop chain. Cleared on unmount (session end).
  useEffect(() => {
    const { setReinitForDeviceChange } = useMicPermissionStore.getState();
    setReinitForDeviceChange(onSilenceDeviceConfirmed);
    return () => setReinitForDeviceChange(null);
  }, [onSilenceDeviceConfirmed]);

  // Resume the AudioContext and (re)build the graph if needed. Shared by the
  // arm path (key-down) and the plain start path (mouse / synthetic press).
  const ensureCaptureReady = useCallback(async () => {
    const audioContext = audioContextRef.current;
    if (audioContext?.state === "suspended") {
      try {
        await audioContext.resume();
      } catch (err) {
        addLog("AudioContext resume failed: " + err);
      }
    }

    if (
      isAudioGraphStale({
        isInitialized: isInitializedRef.current,
        mediaStream: mediaStreamRef.current,
        audioContext: audioContextRef.current,
        workletNode: workletNodeRef.current,
        sourceNode: sourceRef.current,
      })
    ) {
      addLog("Audio graph stale - reinitializing");
      cleanup();
      await initializeAudio();
    }

    if (audioContextRef.current?.state === "suspended") {
      try {
        await audioContextRef.current.resume();
      } catch (err) {
        addLog("AudioContext resume failed after reinit: " + err);
      }
    }

    if (!isInitializedRef.current) {
      await initializeAudio();
    }

    return isInitializedRef.current;
  }, [addLog, cleanup, initializeAudio]);

  /**
   * Optimistic capture: start the worklet and buffer audio locally *without*
   * opening a turn, on key-down and before tap-vs-hold is known. A hold
   * commits the buffer into the new turn; a tap discards it. Nothing leaves
   * the browser while armed.
   */
  const armRecording = useCallback(async () => {
    try {
      if (isRecordingRef.current) return;
      if (captureGateRef.current.state === GATE_ARMED) return;
      // Permission dialog still open — let the threshold path surface the
      // modal; arming silently here would flash "listening" over it.
      if (isInitializingRef.current) return;

      captureGateRef.current.arm();
      pressStartTimeRef.current = Date.now();
      turnHadNonZeroSampleRef.current = false;
      addLog("Arming capture (pre-roll buffer)...");

      const ready = await ensureCaptureReady();
      const gateState = captureGateRef.current.state;
      if (!ready) {
        addLog("Arm cancelled - audio initialization failed");
        // If a press already committed this arm (cold init outlasted the
        // threshold), the turn is open but capture can never start — fail the
        // recording the same way startRecording's init-failure path does.
        if (gateState !== GATE_ARMED) {
          isRecordingRef.current = false;
          setIsRecording(false);
        }
        captureGateRef.current.reset();
        return;
      }
      // Aborted while the graph was warming up (quick tap): nothing to start.
      if (gateState === GATE_IDLE) return;
      // Two survivors reach here: still ARMED (key down, no turn yet), or
      // already committed by a press that beat this async path — cold init can
      // outlast the threshold, and that commit skipped the worklet start on the
      // promise that this continuation performs it. But committed ≠ still held:
      // if the key also came up during warm-up, its stop already ran against a
      // worklet that never started, so starting now would capture with nothing
      // left to end it. Only start while the press is genuinely live.
      if (gateState !== GATE_ARMED && !isRecordingRef.current) {
        addLog("Arm continuation abandoned — the press already ended");
        captureGateRef.current.reset();
        return;
      }
      captureIdRef.current += 1;
      workletNodeRef.current?.port.postMessage({
        type: "start",
        captureId: captureIdRef.current,
      });
      addLog("Worklet armed and capturing");
    } catch (err) {
      addLog("Error in armRecording: " + err);
      // abort() is a no-op once the gate is LIVE, so a throw after a press had
      // already committed would leave a turn open with no capture behind it and
      // no DONE to close it. Mirror the !ready branch: fail the recording.
      if (captureGateRef.current.state !== GATE_ARMED) {
        isRecordingRef.current = false;
        setIsRecording(false);
      }
      captureGateRef.current.reset();
    }
  }, [addLog, ensureCaptureReady, setIsRecording]);

  /** Tap: discard the pre-roll buffer and stop the worklet. No turn existed. */
  const abortArmedRecording = useCallback(() => {
    if (!captureGateRef.current.abort()) return;
    pressStartTimeRef.current = 0;
    workletNodeRef.current?.port.postMessage({ type: "stop" });
    addLog("Aborted armed capture - pre-roll discarded");
  }, [addLog]);

  /**
   * Open a turn and start streaming mic audio.
   *
   * `speechSnapshot` — playback state captured by the caller before it did
   * anything that stops playback. Callers that stop nothing may omit it; see
   * utils/tutorSpeechSnapshot.js.
   */
  const startRecording = useCallback(
    async (speechSnapshot) => {
      // Above the branch so no start path can skip it.
      const snapshot = speechSnapshot ?? buildTutorSpeechSnapshot();
      try {
        // Cancel any pending debounced stop - user wants to continue recording
        if (stopDebounceTimerRef.current) {
          clearTimeout(stopDebounceTimerRef.current);
          stopDebounceTimerRef.current = null;
          // The release that scheduled that stop is void now - clear its timestamp
          // so it can't be mistaken for the eventual release when recording ends.
          releaseTimeRef.current = 0;
          addLog("Cancelled pending stop - extending recording");
        }

        // Already recording, nothing to do
        if (isRecordingRef.current) {
          addLog("Already recording, ignoring start request");
          return;
        }

        // Browser permission dialog is still open - show warning modal
        if (isInitializingRef.current) {
          addLog("Mic permission dialog pending - showing mic modal");
          showMicModal();
          return;
        }

        // Armed fast-path: capture has been running since key-down, the graph is
        // warm, and the worklet is (or is about to be) started by armRecording's
        // continuation. Open the turn and flush the pre-roll into it. TURN_CHANGE
        // dispatches synchronously (new turn id, interrupt, WS send) before the
        // flush, so the buffered chunks land inside the new turn.
        if (captureGateRef.current.state === GATE_ARMED) {
          isRecordingRef.current = true;
          setIsRecording(true);
          shouldStopRef.current = false;
          addLog("Committing armed capture...");
          emit("TURN_CHANGE", { source: "mic", ...snapshot });
          const flushed = captureGateRef.current.commit((msg) =>
            emit("USER_AUDIO_CHUNK", msg),
          );
          addLog(`Recording started (flushed ${flushed} pre-roll chunk(s))`);
          return;
        }

        // Set recording flag immediately to prevent race conditions
        // Multiple rapid calls could otherwise pass the above check before any sets this flag
        isRecordingRef.current = true;
        setIsRecording(true);

        shouldStopRef.current = false;
        pressStartTimeRef.current = Date.now();
        turnHadNonZeroSampleRef.current = false;
        addLog("Starting recording...");
        emit("TURN_CHANGE", { source: "mic", ...snapshot });
        // No pre-roll on this path (mouse / synthetic press) — go straight live.
        captureGateRef.current.commit((msg) => emit("USER_AUDIO_CHUNK", msg));

        const ready = await ensureCaptureReady();
        if (!ready) {
          addLog("Recording cancelled - audio initialization failed");
          isRecordingRef.current = false;
          setIsRecording(false);
          captureGateRef.current.reset();
          return;
        }

        if (DEBUG_AUDIO) {
          try {
            await startRecorderRecording();
          } catch (err) {
            addLog("Error starting debug recorder: " + err);
            isRecordingRef.current = false;
            setIsRecording(false);
            captureGateRef.current.reset();
            return;
          }
        }

        if (shouldStopRef.current) {
          addLog("Recording cancelled after recorder start");
          isRecordingRef.current = false;
          setIsRecording(false);
          captureGateRef.current.reset();
          return;
        }

        if (workletNodeRef.current && isInitializedRef.current) {
          captureIdRef.current += 1;
          workletNodeRef.current.port.postMessage({
            type: "start",
            captureId: captureIdRef.current,
          });
          addLog("Sent start message to worklet");
          addLog("Recording started...");
        } else {
          addLog("Recording cancelled - worklet not ready");
          isRecordingRef.current = false;
          setIsRecording(false);
          captureGateRef.current.reset();
        }
      } catch (err) {
        addLog("Error in startRecording: " + err);
        isRecordingRef.current = false;
        setIsRecording(false);
        captureGateRef.current.reset();
        const errorName = getErrorName(err);
        if (isPermissionDeniedErrorName(errorName)) {
          setMicPermissionDenied(true);
        }
        if (isKnownMicErrorName(errorName)) {
          showMicModal();
        }
      }
    },
    [
      addLog,
      emit,
      ensureCaptureReady,
      setMicPermissionDenied,
      setIsRecording,
      showMicModal,
      startRecorderRecording,
    ],
  );

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // Tab hidden - stop immediately, but keep audio graph intact
        addLog("Page hidden - stopping active recording (keeping audio ready)");
        abortArmedRecording(); // discard any pre-roll — the key-up is lost
        stopRecording(true); // immediate stop
      } else {
        // Tab visible again - resume AudioContext if it was suspended
        addLog("Page visible - resuming audio context");
        if (audioContextRef.current?.state === "suspended") {
          try {
            await audioContextRef.current.resume();
            addLog("AudioContext resumed successfully");
          } catch (err) {
            addLog("AudioContext resume failed: " + err);
          }
        }
      }
    };

    const handlePageHide = () => {
      // Only do full cleanup on actual page unload, not tab switch
      addLog("Page hidden (pagehide) - stopping mic and cleaning up");
      abortArmedRecording();
      stopRecording(true); // immediate stop
      cleanup();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [addLog, cleanup, stopRecording, abortArmedRecording]);

  // Stable refs so the permissions useEffect never needs to re-run
  const cleanupRef = useRef(cleanup);
  const initializeAudioRef = useRef(initializeAudio);
  const closeModalRef = useRef(closeModal);
  const setMicPermissionDeniedRef = useRef(setMicPermissionDenied);
  useEffect(() => {
    cleanupRef.current = cleanup;
  }, [cleanup]);
  useEffect(() => {
    initializeAudioRef.current = initializeAudio;
  }, [initializeAudio]);
  useEffect(() => {
    closeModalRef.current = closeModal;
  }, [closeModal]);
  useEffect(() => {
    setMicPermissionDeniedRef.current = setMicPermissionDenied;
  }, [setMicPermissionDenied]);

  // Released when the player unmounts, as on pagehide. In-app navigation --
  // back to the list, or Next lesson remounting the player for the next lesson
  // -- otherwise left the stream and its AudioContext open, one more for every
  // lesson, and a browser caps how many AudioContexts one page may hold.
  useEffect(() => () => cleanupRef.current(), []);

  useEffect(() => {
    if (!navigator.permissions) return;
    let permStatus = null;

    navigator.permissions
      .query({ name: "microphone" })
      .then((status) => {
        permStatus = status;
        setMicPermissionDeniedRef.current(
          isPermissionDeniedState(status.state),
        );
        status.onchange = () => {
          const denied = isPermissionDeniedState(status.state);
          setMicPermissionDeniedRef.current(denied);
          if (status.state === "granted") {
            if (useModalStore.getState().modal === ModalType.MIC_PERMISSION) {
              closeModalRef.current();
            }
            if (!isInitializedRef.current && !isInitializingRef.current) {
              cleanupRef.current();
              initializeAudioRef.current().catch(() => {});
            }
          }
        };
      })
      .catch(() => {});

    return () => {
      if (permStatus) permStatus.onchange = null;
    };
  }, []);

  // Stable wrappers so callers never hold a stale closure while event listeners
  // remain registered across mic/runtime changes.
  const retryMicPermissionStable = useCallback(
    (...args) => retryMicPermissionRef.current(...args),
    [],
  );

  const startRecordingRef = useRef(startRecording);
  useEffect(() => {
    startRecordingRef.current = startRecording;
  }, [startRecording]);
  const startRecordingStable = useCallback(
    (...args) => startRecordingRef.current(...args),
    [],
  );

  const armRecordingRef = useRef(armRecording);
  useEffect(() => {
    armRecordingRef.current = armRecording;
  }, [armRecording]);
  const armRecordingStable = useCallback(
    (...args) => armRecordingRef.current(...args),
    [],
  );

  const abortArmedRecordingRef = useRef(abortArmedRecording);
  useEffect(() => {
    abortArmedRecordingRef.current = abortArmedRecording;
  }, [abortArmedRecording]);
  const abortArmedRecordingStable = useCallback(
    (...args) => abortArmedRecordingRef.current(...args),
    [],
  );

  return {
    startRecording: startRecordingStable,
    armRecording: armRecordingStable,
    abortArmedRecording: abortArmedRecordingStable,
    stopRecording,
    cleanup,
    retryMicPermission: retryMicPermissionStable,
    reinitForDeviceChange: onSilenceDeviceConfirmed,
    isRecorderRecording,
    audioBlob,
  };
};
